"use client";

/**
 * useProofKYC - Hook for DFlow Proof KYC verification
 *
 * Checks if the user's Solana wallet is verified via DFlow Proof.
 * Required for purchasing Kalshi prediction markets via DFlow.
 * Users who are not verified can only view markets, not buy.
 */

import { useState, useEffect, useCallback } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useWallets, useSignMessage } from "@privy-io/react-auth/solana";
import { authFetch } from "@/lib/authFetch";

interface ProofKYCState {
  /** Whether the wallet is verified via Proof KYC */
  verified: boolean;
  /** Whether the verification check is in progress */
  loading: boolean;
  /** The user's Solana wallet address (null if not available) */
  solanaAddress: string | null;
  /** Whether the user is logged in */
  authenticated: boolean;
  /** DFlow account status — distinct from KYC. KYC is permanent; status flips
   *  to 'disabled' on Revoke and back to 'active' on Re-enable. UI uses this
   *  to choose between Active/Revoke vs Inactive/Re-enable. */
  accountStatus: 'active' | 'disabled' | 'missing';
  /** Start the Proof KYC verification flow (redirects to dflow.net/proof) */
  startVerification: () => Promise<void>;
  /** Re-check verification status */
  refresh: () => void;
}

export function useProofKYC(): ProofKYCState {
  const { authenticated, ready } = usePrivy();
  const { wallets } = useWallets();
  const { signMessage } = useSignMessage();
  const [verified, setVerified] = useState(false);
  const [accountStatus, setAccountStatus] = useState<'active' | 'disabled' | 'missing'>('missing');
  const [loading, setLoading] = useState(true);
  // The backend-resolved wallet is authoritative: it's the wallet that
  // actually holds the user's DFlow account + positions. `wallets[0]`
  // can point to a different entry if the user has multiple Solana
  // wallets linked, which would cause the UI to show "verified/not
  // registered" against a wallet that isn't the one trading.
  const [backendAddress, setBackendAddress] = useState<string | null>(null);

  // Client-side first wallet (only used before backend resolves, or as
  // a fallback when the user isn't yet registered with DFlow).
  const solanaWallet = wallets[0] ?? null;
  const clientAddress = solanaWallet?.address || null;
  const solanaAddress = backendAddress || clientAddress;

  // SECURITY [H-02]: Use authFetch — backend resolves wallet from auth, no address param
  const checkVerification = useCallback(async () => {
    if (!authenticated) {
      setLoading(false);
      setVerified(false);
      setBackendAddress(null);
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`/api/dflow/proof-status`);
      const data = await res.json();
      setVerified(data.verified === true);
      if (data.accountStatus === 'active' || data.accountStatus === 'disabled' || data.accountStatus === 'missing') {
        setAccountStatus(data.accountStatus);
      }
      if (typeof data.address === 'string' && data.address) {
        setBackendAddress(data.address);
      }
    } catch {
      setVerified(false);
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  // Check verification status when authenticated
  useEffect(() => {
    if (!ready) return; // Privy still initializing — keep loading=true
    if (!authenticated) {
      setLoading(false); // Not logged in — nothing to check
      return;
    }
    checkVerification();
  }, [ready, authenticated, checkVerification]);

  // Detect return from Proof KYC flow and re-check with retries
  // DFlow Proof may redirect with ?proof_return=true (our param) or ?status=verified (DFlow's param)
  useEffect(() => {
    if (!authenticated || typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const isProofReturn = params.get("proof_return") === "true" || params.get("status") === "verified";
    if (!isProofReturn) return;

    // Clean URL
    params.delete("proof_return");
    params.delete("status");
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);

    // Re-check with invalidation + retries (1s, 5s)
    const checkWithRetry = async (delay: number) => {
      await new Promise((r) => setTimeout(r, delay));
      try {
        const res = await authFetch(`/api/dflow/proof-status?invalidate=true`);
        const data = await res.json();
        if (data.verified === true) {
          setVerified(true);
          return true;
        }
      } catch { /* ignore */ }
      return false;
    };

    (async () => {
      const first = await checkWithRetry(1000);
      if (!first) await checkWithRetry(5000);
    })();
  }, [authenticated]);

  const startVerification = useCallback(async () => {
    if (!solanaWallet || !solanaAddress) return;

    const timestamp = Date.now();
    const message = `Proof KYC verification: ${timestamp}`;

    try {
      const encoder = new TextEncoder();
      const messageBytes = encoder.encode(message);

      // Sign using Privy's Solana signMessage hook
      const { signature: signatureBytes } = await signMessage({
        message: messageBytes,
        wallet: solanaWallet,
      });

      // Encode signature as base58
      const signature = base58Encode(signatureBytes);

      // Build the redirect URI (return to current page with proof_return flag)
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set("proof_return", "true");
      const redirectUri = encodeURIComponent(returnUrl.toString());

      // Redirect to DFlow Proof
      window.location.href = `https://dflow.net/proof?wallet=${solanaAddress}&signature=${signature}&timestamp=${timestamp}&redirect_uri=${redirectUri}`;
    } catch {
      // User rejected the signature or wallet error — do nothing
    }
  }, [solanaWallet, solanaAddress, signMessage]);

  return {
    verified,
    loading,
    solanaAddress,
    accountStatus,
    authenticated: ready && authenticated,
    startVerification,
    refresh: checkVerification,
  };
}

/**
 * Simple Base58 encoder (Bitcoin alphabet)
 * Used to encode Solana wallet signatures for Proof deep links
 */
function base58Encode(bytes: Uint8Array): string {
  const ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  const digits = [0];

  for (const byte of bytes) {
    let carry = byte;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = (carry / 58) | 0;
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = (carry / 58) | 0;
    }
  }

  // Leading zeros
  for (const byte of bytes) {
    if (byte !== 0) break;
    digits.push(0);
  }

  return digits
    .reverse()
    .map((d) => ALPHABET[d])
    .join("");
}
